'use client';

import { useState, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AxiosError } from 'axios';
import { Clock, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import axiosInstance from '@/lib/axios';
import { reportCreatePostFailure } from '@/lib/capture-create-post-error';
import { uploadFilesToVertex } from '@/lib/vertex-upload';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { ImageFileInput } from '@/components/forms/image-file-input';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { useUser } from '@/hooks/use-user';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormMultiSelectField } from '@/components/forms/form-multi-select-field';
import { FormRadioGroupField } from '@/components/forms/form-radio-group-field';
import { FormWheelDatePickerField } from '@/components/forms/form-wheel-date-picker-field';
import { SectionLabel } from '@/components/forms/section-label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const meetupSchema = z
  .object({
    initiatorId: z.string().min(1, 'Please select an initiator'),
    participantIds: z.array(z.string()).min(1, 'Please select at least one participant'),
    type: z.enum(['ONE_TO_ONE', 'GROUP_MEETING', 'POWER_VISIT']),
    meetingMode: z.enum(['ONLINE', 'OFFLINE']),
    location: z.string().optional(),
    otherLocation: z.string().optional(),
    date: z.string().min(1, 'Date is required'),
    time: z.string().min(1, 'Time is required'),
    duration: z.string().optional(),
    message: z.string().max(500, 'Message too long').optional(),
    powerVisitDetails: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'ONE_TO_ONE' && data.participantIds.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only one participant allowed for One-to-One meetings',
        path: ['participantIds'],
      });
    }

    if (data.meetingMode === 'OFFLINE') {
      if (!data.location || data.location.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Location is required for offline meetings',
          path: ['location'],
        });
      } else if (
        data.location === 'OTHER' &&
        (!data.otherLocation || data.otherLocation.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please specify the other location',
          path: ['otherLocation'],
        });
      }
    }
  });

const MEETING_TYPE_OPTIONS = [
  { value: 'ONE_TO_ONE', label: 'One-to-One (1-2-1)' },
  { value: 'GROUP_MEETING', label: 'Group Meeting' },
  { value: 'POWER_VISIT', label: 'Power Visit' },
];

const MEETING_MODE_OPTIONS = [
  { value: 'OFFLINE', label: 'Offline (In-Person)' },
  { value: 'ONLINE', label: 'Online (Virtual)' },
];

export type MeetupFormValues = z.infer<typeof meetupSchema>;

export interface MeetupEditTarget {
  postId: string;
  communityId: string;
  initialValues: MeetupFormValues;
}

interface MeetupFormProps {
  onCancel: () => void;
  onSubmit: (postId: string) => void;
  editing?: MeetupEditTarget;
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return (error.response?.data as { error?: string } | undefined)?.error;
  }
  return undefined;
};

export function MeetupForm({ onCancel, onSubmit: parentSubmit, editing }: MeetupFormProps) {
  const params = useParams<{ communityId: string }>();
  const communityId = editing?.communityId ?? params.communityId;
  const { members } = useCommunityMembers(communityId!);
  const { user: currentUser } = useUser();

  const form = useForm<MeetupFormValues>({
    resolver: zodResolver(meetupSchema),
    mode: 'onChange',
    defaultValues: editing?.initialValues ?? {
      initiatorId: currentUser?.id || '',
      participantIds: [],
      type: 'ONE_TO_ONE',
      meetingMode: 'OFFLINE',
      location: '',
      otherLocation: '',
      date: new Date().toISOString().split('T')[0],
      time: '',
      duration: '',
      message: '',
      powerVisitDetails: '',
    },
  });

  const [searchParams] = useSearchParams();
  const prefillMemberId = searchParams.get('memberId');

  useEffect(() => {
    if (prefillMemberId && !editing) {
      form.setValue('participantIds', [prefillMemberId]);
    }
  }, [prefillMemberId, editing, form]);

  const [photos, setPhotos] = useState<File[]>([]);
  const [preparingPhotos, setPreparingPhotos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');
  const [showTypeChangeDialog, setShowTypeChangeDialog] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<{
    value: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fieldOnChange: (...event: any[]) => void;
  } | null>(null);

  const initiatorId = useWatch({ control: form.control, name: 'initiatorId' });
  const participantIds = useWatch({ control: form.control, name: 'participantIds' });
  const meetingMode = useWatch({ control: form.control, name: 'meetingMode' });
  const type = useWatch({ control: form.control, name: 'type' });
  const location = useWatch({ control: form.control, name: 'location' });

  const initiatorOptions = useMemo(
    () =>
      members.map((member) => ({
        label: `${member.name}${member.id === currentUser?.id ? ' (Me)' : ''}`,
        value: member.id,
      })),
    [members, currentUser?.id]
  );

  const memberOptions = useMemo(() => {
    return members
      .filter((m) => m.id !== initiatorId)
      .map((member) => ({
        label: member.name,
        value: member.id,
      }));
  }, [members, initiatorId]);

  const locationOptions = useMemo(() => {
    const options = participantIds.map((id) => {
      const member = members.find((m) => m.id === id);
      return {
        label: `${member?.name}'s place`,
        value: `${member?.name}'s place`,
      };
    });

    const initiator = members.find((m) => m.id === initiatorId);
    if (initiator) {
      options.unshift({
        label: `${initiator.name}'s place`,
        value: `${initiator.name}'s place`,
      });
    }

    options.push({ label: 'Other', value: 'OTHER' });
    return options;
  }, [participantIds, members, initiatorId]);

  const handlePreparedPhotos = (files: File[]) => {
    if (photos.length + files.length > 2) {
      toast.error('Maximum 2 photos allowed');
      return;
    }
    setPhotos([...photos, ...files]);
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!editing || members.length === 0) return;
    const currentLocation = form.getValues('location');
    if (!currentLocation || currentLocation === 'OTHER') return;
    if (locationOptions.some((option) => option.value === currentLocation)) return;
    form.setValue('otherLocation', currentLocation);
    form.setValue('location', 'OTHER');
  }, [editing, members.length, locationOptions, form]);

  const onSubmitEdit = async (values: MeetupFormValues, target: MeetupEditTarget) => {
    const finalLocation = values.location === 'OTHER' ? 'Other' : values.location;
    setIsSubmitting(true);
    setSubmitPhase('saving');

    try {
      await axiosInstance.patch(`/api/posts/${target.postId}`, {
        participantIds: values.participantIds,
        initiatorId: values.initiatorId,
        type: values.type,
        meetingMode: values.meetingMode,
        location: finalLocation || null,
        otherLocation: values.location === 'OTHER' ? values.otherLocation || null : null,
        date: values.date,
        time: values.time,
        duration: values.duration || null,
        message: values.message || null,
        powerVisitDetails: values.powerVisitDetails || null,
      });
      toast.success('Meetup updated');
      parentSubmit(target.postId);
    } catch (error) {
      console.error('Edit meetup failed:', error);
      toast.error(getErrorMessage(error) ?? 'Failed to update meetup');
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  const onSubmit = async (values: MeetupFormValues) => {
    if (editing) {
      await onSubmitEdit(values, editing);
      return;
    }

    setIsSubmitting(true);
    setSubmitPhase(photos.length > 0 ? 'uploading' : 'saving');

    try {
      const photoUrls = photos.length > 0 ? await uploadFilesToVertex(photos, 'meetup') : [];

      const formData = new FormData();
      formData.append('participantIds', JSON.stringify(values.participantIds));
      formData.append('initiatorId', values.initiatorId);
      formData.append('type', values.type);
      formData.append('meetingMode', values.meetingMode);

      const finalLocation = values.location === 'OTHER' ? 'Other' : values.location;
      if (finalLocation) {
        formData.append('location', finalLocation);
      }
      if (values.location === 'OTHER' && values.otherLocation) {
        formData.append('otherLocation', values.otherLocation);
      }
      if (values.powerVisitDetails) {
        formData.append('powerVisitDetails', values.powerVisitDetails);
      }

      formData.append('date', values.date);
      formData.append('time', values.time);
      if (values.duration) formData.append('duration', values.duration);
      if (values.message) formData.append('message', values.message);
      formData.append('attachments', JSON.stringify(photoUrls));

      setSubmitPhase('saving');
      const { data } = await axiosInstance.post(
        `/api/communities/${communityId}/posts/meetup`,
        formData
      );
      toast.success('Meetup Recorded!');
      parentSubmit(data.postId);
    } catch (error) {
      const finalLocation = values.location === 'OTHER' ? 'Other' : values.location;
      toast.error(
        reportCreatePostFailure({
          kind: 'meetup',
          error,
          userId: currentUser?.id,
          communityId,
          request: {
            initiatorId: values.initiatorId,
            participantIds: values.participantIds,
            type: values.type,
            meetingMode: values.meetingMode,
            location: finalLocation ?? null,
            otherLocation: values.otherLocation ?? null,
            date: values.date,
            time: values.time,
            duration: values.duration ?? null,
            hasMessage: Boolean(values.message),
            powerVisitDetails: values.powerVisitDetails ?? null,
            photoCount: photos.length,
            photoNames: photos.map((file) => file.name),
          },
          fallbackMessage: 'Failed to record meetup. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  const submitLabel = isSubmitting
    ? editing
      ? 'Saving...'
      : 'Recording...'
    : editing
      ? 'Save Changes'
      : 'Record Meetup';

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title={
          submitPhase === 'uploading'
            ? 'Uploading photos...'
            : editing
              ? 'Saving meetup...'
              : 'Recording meetup...'
        }
        description={
          submitPhase === 'uploading'
            ? 'Sending images through secure media upload. Please keep this screen open.'
            : 'Saving your meetup details.'
        }
      />
      <Card className="mx-auto w-full max-w-5xl animate-in slide-in-from-bottom-5 border-0 bg-background/50 shadow-none backdrop-blur-md duration-300 md:border md:border-border md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="flex items-center gap-2 text-2xl font-black tracking-tight text-foreground">
            {editing ? 'Edit Meetup' : 'Announce a Meetup'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {editing
              ? 'Update the details of this meetup.'
              : 'Met up with fellow members? Share the details with the community.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormSelectField
                control={form.control}
                name="initiatorId"
                label="Who initiated this meeting?"
                placeholder="Select Initiator"
                required
                options={initiatorOptions}
              />

              <FormRadioGroupField
                control={form.control}
                name="type"
                label="What type of meeting was it?"
                required
                layout="row"
                options={MEETING_TYPE_OPTIONS}
                onChange={(newType, fieldOnChange) => {
                  if (
                    newType === 'ONE_TO_ONE' &&
                    (type === 'GROUP_MEETING' || type === 'POWER_VISIT') &&
                    participantIds.length > 1
                  ) {
                    setPendingTypeChange({ value: newType, fieldOnChange });
                    setShowTypeChangeDialog(true);
                  } else {
                    fieldOnChange(newType);
                  }
                }}
              />

              {type === 'POWER_VISIT' && (
                <FormInputField
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={form.control as any}
                  name="powerVisitDetails"
                  label="Details of company you visited"
                  placeholder="Enter Company Name"
                  required
                />
              )}

              <FormMultiSelectField
                control={form.control}
                name="participantIds"
                label={type === 'ONE_TO_ONE' ? 'Who did you meet?' : 'Who were the participants?'}
                placeholder={type === 'ONE_TO_ONE' ? 'Select Participant' : 'Select Participants'}
                required
                options={memberOptions}
                maxSelected={type === 'ONE_TO_ONE' ? 1 : undefined}
                creatable={false}
                description={
                  type === 'ONE_TO_ONE' && participantIds.length > 0
                    ? 'One-to-One: Only 1 participant can be selected.'
                    : undefined
                }
              />

              <FormRadioGroupField
                control={form.control}
                name="meetingMode"
                label="Meeting Format"
                required
                layout="row"
                options={MEETING_MODE_OPTIONS}
              />

              {meetingMode === 'OFFLINE' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <FormSelectField
                    control={form.control}
                    name="location"
                    label="Where did you meet?"
                    placeholder="Select Location"
                    required
                    options={locationOptions}
                  />

                  {location === 'OTHER' && (
                    <FormInputField
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      control={form.control as any}
                      name="otherLocation"
                      label="Specify Location"
                      placeholder="Cafe, Co-working space, etc."
                      className="animate-in zoom-in-95 duration-200"
                    />
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormWheelDatePickerField
                  control={form.control}
                  name="date"
                  label="Date of Meeting"
                  placeholder="Select Date"
                  required
                />
                <FormInputField
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={form.control as any}
                  name="time"
                  label="Time of Meeting"
                  placeholder="Select Time"
                  type="time"
                  required
                  icon={<Clock className="h-4 w-4" />}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormInputField
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={form.control as any}
                  name="duration"
                  label="Duration (Optional)"
                  placeholder="e.g. 1 hour, 45 mins"
                />
                <FormTextareaField
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={form.control as any}
                  name="message"
                  label="Quick Summary (Optional)"
                  placeholder="What did you discuss? Use @ for a member or business, # for a category"
                  maxLength={500}
                  mentions
                />
              </div>

              {!editing && (
                <div className="space-y-3">
                  <SectionLabel>Photos (Max 2, Optional)</SectionLabel>
                  <div className="flex flex-wrap gap-4">
                    {photos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative h-24 w-24 overflow-hidden rounded-lg border"
                      >
                        <img
                          src={URL.createObjectURL(photo)}
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removePhoto(index)}
                          className="absolute right-1 top-1 h-7 w-7 rounded-full bg-black/50 text-white hover:bg-black/70"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {photos.length < 2 && (
                      <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors hover:bg-muted/50">
                        {preparingPhotos ? (
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        ) : (
                          <Plus className="h-6 w-6 text-muted-foreground" />
                        )}
                        <span className="mt-1 text-center text-[10px] font-medium text-muted-foreground">
                          {preparingPhotos ? 'Preparing' : 'Add Photo'}
                        </span>
                        <ImageFileInput
                          accept="image/*"
                          className="hidden"
                          multiple={photos.length === 0}
                          disabled={isSubmitting || preparingPhotos}
                          onPreparingChange={setPreparingPhotos}
                          onPreparedFiles={handlePreparedPhotos}
                        />
                      </label>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!form.formState.isValid || isSubmitting}
                  className="h-11 w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
                >
                  {submitLabel}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
        <AlertDialog open={showTypeChangeDialog} onOpenChange={setShowTypeChangeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Meeting Type?</AlertDialogTitle>
              <AlertDialogDescription>
                One-to-One meetings can only have one participant. Changing the meeting type will
                remove all currently selected participants. Are you sure you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setPendingTypeChange(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingTypeChange) {
                    pendingTypeChange.fieldOnChange(pendingTypeChange.value);
                    form.setValue('participantIds', []);
                    setPendingTypeChange(null);
                  }
                }}
              >
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </>
  );
}
